import { convertNumber, GeckoMeasurementUnit } from 'types'

// Zone values are stored as plain numbers in fixed metric units — the field
// names carry them (`_mm`, `_bar`, `_c`) and gong-agent-service reads them that
// way. Inputs convert only to display and edit, so the measurement toggle never
// rewrites stored data.

/**
 * A stored zone value in `display`, rounded to 6 significant digits. Fixed
 * decimal places would hide small rates, and the input box re-renders this
 * value on every keystroke, so it also caps what the SME can type.
 */
export const toDisplayValue = (
  value: number | undefined,
  stored: GeckoMeasurementUnit,
  display: GeckoMeasurementUnit
): number | undefined =>
  value === undefined
    ? undefined
    : Number(convertNumber(value, stored, display).toPrecision(6))

/** An edited input value, given in `display`, back in its stored unit. */
export const toStoredValue = (
  value: number | null,
  stored: GeckoMeasurementUnit,
  display: GeckoMeasurementUnit
): number | undefined =>
  value === null ? undefined : convertNumber(value, display, stored)

// The units gong-agent-service's `DamageMechanismItem.rate_unit` allows; a
// blank unit is the schema's 'mm/y' default
const RATE_UNITS = new Map<string, GeckoMeasurementUnit>([
  ['', GeckoMeasurementUnit.MillimetersPerYear],
  ['mm/y', GeckoMeasurementUnit.MillimetersPerYear],
  ['mpy', GeckoMeasurementUnit.MilsPerYear],
])

/**
 * A damage mechanism's `rate_unit` as a unit. Undefined for anything else —
 * callers must then leave the value unconverted rather than guess.
 */
export const storedRateUnit = (
  rateUnit: string | undefined
): GeckoMeasurementUnit | undefined => RATE_UNITS.get(rateUnit ?? '')

/** The `rate_unit` a rate typed in `display` is saved under, unconverted. */
export const rateUnitCode = (display: GeckoMeasurementUnit): 'mm/y' | 'mpy' =>
  display === GeckoMeasurementUnit.MilsPerYear ? 'mpy' : 'mm/y'
